import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { api, User, Modules } from '../lib/api';

interface AuthContextType {
  user: User | null;
  modules: Modules;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

const defaultModules: Modules = {
  ticketingEnabled: true
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [modules, setModules] = useState<Modules>(defaultModules);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchUser = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getMe();
      setUser(response.user);
      setModules(response.modules || defaultModules);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch user');
      setUser(null);
      setModules(defaultModules);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUser();
  }, []);

  return (
    <AuthContext.Provider value={{ user, modules, loading, error, refetch: fetchUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
